import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Trash2, KeyRound, UserPlus, ShieldCheck, ShieldAlert, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const ROLE_META = {
  admin:  { label: "Admin",  color: "bg-emerald-50 text-emerald-700 border-emerald-200", desc: "Full access, including user management & settings." },
  editor: { label: "Editor", color: "bg-sky-50 text-sky-700 border-sky-200",             desc: "Can create/edit content but no user management or settings." },
  viewer: { label: "Viewer", color: "bg-zinc-100 text-zinc-700 border-zinc-200",         desc: "Read-only access to the CMS." },
};

const emptyForm = { email: "", password: "", name: "", role: "editor" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [resetFor, setResetFor] = useState(null); // user id being password-reset
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error("Email and password required");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    } finally { setSaving(false); }
  };

  const updateRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { active: !(u.active !== false) });
      toast.success(u.active === false ? "User activated" : "User deactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const resetPassword = async (u) => {
    if (!newPw || newPw.length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.put(`/admin/users/${u.id}`, { password: newPw });
      toast.success("Password reset");
      setResetFor(null);
      setNewPw("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Team</div>
          <h1 className="font-display text-4xl mt-3">Users & Roles</h1>
          <p className="text-sm text-zinc-500 mt-2">Add teammates, control what they can access, and manage passwords.</p>
        </div>
      </div>

      {/* Role legend */}
      <div className="mt-6 grid md:grid-cols-3 gap-3">
        {Object.entries(ROLE_META).map(([k, v]) => (
          <div key={k} className={`border rounded-md p-4 ${v.color}`}>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {k === "admin" && <ShieldCheck className="w-4 h-4" />}
              {k === "editor" && <ShieldAlert className="w-4 h-4" />}
              {k === "viewer" && <ShieldOff className="w-4 h-4" />}
              {v.label}
            </div>
            <div className="text-xs mt-1 opacity-80">{v.desc}</div>
          </div>
        ))}
      </div>

      {/* Create user */}
      <form onSubmit={createUser} className="mt-8 bg-white border border-zinc-200 rounded-md p-5" data-testid="create-user-form">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-4 h-4 text-zinc-700" />
          <h2 className="font-semibold text-sm">Add new user</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
          <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
          <Input type="password" placeholder="Password (min 8 chars) *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
          <select className="border rounded-md px-3 h-10 text-sm bg-white" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
            <option value="admin">Admin</option>
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        <div className="mt-4">
          <button type="submit" disabled={saving} className="bg-zinc-900 text-white px-4 py-2 text-sm rounded-md disabled:opacity-60" data-testid="create-user-btn">
            {saving ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>

      {/* Users list */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Role</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Created</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">No users yet.</td></tr>}
            {users.map((u) => {
              const isSelf = me?.id === u.id;
              const active = u.active !== false;
              return (
                <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.email}`}>
                  <td className="p-3 font-medium">{u.name || "—"} {isSelf && <span className="text-[10px] text-zinc-500 ml-1">(you)</span>}</td>
                  <td className="p-3 text-zinc-700">{u.email}</td>
                  <td className="p-3">
                    <select value={u.role} onChange={(e) => updateRole(u, e.target.value)} disabled={isSelf}
                      className={`border rounded-md px-2 py-1 text-xs ${ROLE_META[u.role]?.color || ""} disabled:opacity-60`}
                      data-testid={`role-select-${u.email}`}>
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </td>
                  <td className="p-3">
                    <button onClick={() => toggleActive(u)} disabled={isSelf}
                      className={`px-2 py-1 rounded text-xs border ${active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-zinc-100 border-zinc-200 text-zinc-600"} disabled:opacity-60`}
                      data-testid={`toggle-active-${u.email}`}>
                      {active ? "Active" : "Deactivated"}
                    </button>
                  </td>
                  <td className="p-3 text-zinc-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {resetFor === u.id ? (
                      <div className="inline-flex items-center gap-2">
                        <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" className="h-8 w-40" data-testid={`new-pw-input-${u.email}`} />
                        <button onClick={() => resetPassword(u)} className="text-xs bg-zinc-900 text-white px-2 py-1 rounded" data-testid={`confirm-reset-${u.email}`}>Save</button>
                        <button onClick={() => { setResetFor(null); setNewPw(""); }} className="text-xs text-zinc-500 px-2 py-1">Cancel</button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => { setResetFor(u.id); setNewPw(""); }} className="text-zinc-700 hover:bg-zinc-100 p-1 rounded" title="Reset password" data-testid={`reset-pw-${u.email}`}>
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(u)} disabled={isSelf} className="text-red-600 hover:bg-red-50 p-1 rounded disabled:opacity-30" title="Delete user" data-testid={`delete-user-${u.email}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
